// src/components/Filiacao/UploadDocumentos.tsx
// UPDATED: adicionado prop opcional `documentosIniciais` para permitir
//          listas customizadas (ex: Revalida Pro). Totalmente retrocompatível.

import React, { useMemo, useState } from 'react';
import { Upload, CheckCircle, FileText, AlertCircle, Loader2 } from 'lucide-react';
import { API_BASE } from '../../utils/apiConfig';
type DocumentType =
  | 'cpf'
  | 'rg'
  | 'comprovante'
  | 'diploma'
  | 'certificado'
  | 'foto'
  | 'homologacao'
  | 'outro';

export interface Documento {
  id: string;
  titulo: string;
  descricao: string;
  arquivo: File | null;
  obrigatorio: boolean;
  tiposAceitos: string[];
  tamanhoMaxMB: number;
  documentType: DocumentType;
  arquivos?: File[];
}

type UploadResult = {
  id: string;
  url: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  documentType: string;
};

// ─── Documentos padrão (Filiação) ───────────────────────────────────────────
const DOCUMENTOS_PADRAO: Documento[] = [
  {
    id: 'doc1',
    titulo: 'Contrato Social (opcional)',
    descricao: 'Documento de constituição da empresa',
    arquivo: null,
    obrigatorio: false,
    tiposAceitos: ['.pdf', '.jpg', '.jpeg', '.png', '.webp'],
    tamanhoMaxMB: 5,
    documentType: 'homologacao',
  },
  {
    id: 'doc2',
    titulo: 'Comprovante de Endereço',
    descricao: 'Conta de luz, água ou telefone recente',
    arquivo: null,
    obrigatorio: true,
    tiposAceitos: ['.pdf', '.jpg', '.jpeg', '.png', '.webp'],
    tamanhoMaxMB: 3,
    documentType: 'comprovante',
  },
  {
    id: 'doc3',
    titulo: 'Documento do Responsável',
    descricao: 'RG ou CNH do responsável técnico',
    arquivo: null,
    obrigatorio: true,
    tiposAceitos: ['.pdf', '.jpg', '.jpeg', '.png', '.webp'],
    tamanhoMaxMB: 3,
    documentType: 'rg',
  },
  {
    id: 'doc4',
    titulo: 'Certificado de Formação',
    descricao: 'Certificado profissional na área de beleza',
    arquivo: null,
    obrigatorio: false,
    tiposAceitos: ['.pdf', '.jpg', '.jpeg', '.png', '.webp'],
    tamanhoMaxMB: 5,
    documentType: 'certificado',
  },
  {
    id: 'doc5',
    titulo: 'Foto de perfil',
    descricao: 'Sua melhor foto para carteirinha',
    arquivo: null,
    obrigatorio: false,
    tiposAceitos: ['.jpg', '.jpeg', '.png', '.webp'],
    tamanhoMaxMB: 5,
    documentType: 'foto',
  },
];

// ─── Props ───────────────────────────────────────────────────────────────────
interface UploadDocumentosProps {
  affiliateId: string | null;
  onDocumentosSalvos: (arquivos: File[]) => void;
  onUploadSucesso?: (results: UploadResult[]) => void;
  onGerarAffiliateId?: () => Promise<{ affiliateId: string; systemId?: string }>;
  onTrackingError?: (code: string, section: string, metadata?: Record<string, unknown>) => void;

  /**
   * Lista de documentos customizada.
   * Se não fornecida, usa os documentos padrão da Filiação.
   * NOTA: os objetos devem ter `arquivo: null` — o estado é gerenciado internamente.
   */
  documentosIniciais?: Omit<Documento, 'arquivo'>[];
}

// ─── Componente ──────────────────────────────────────────────────────────────
const UploadDocumentos: React.FC<UploadDocumentosProps> = ({
  affiliateId,
  onDocumentosSalvos,
  onUploadSucesso,
  onGerarAffiliateId,
  onTrackingError,
  documentosIniciais,
}) => {

  // Usa lista customizada ou padrão, sempre inicializando arquivo como null
  const listaInicial: Documento[] = useMemo(() => {
    if (documentosIniciais && documentosIniciais.length > 0) {
      return documentosIniciais.map((d) => ({ ...d, arquivo: null }));
    }
    return DOCUMENTOS_PADRAO;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [documentos, setDocumentos] = useState<Documento[]>(listaInicial);

  const [erros, setErros] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<Record<string, 'idle' | 'ok' | 'fail'>>({});
  const [localAffiliateId, setLocalAffiliateId] = useState<string | null>(null);

  const effectiveAffiliateId = affiliateId ?? localAffiliateId;

  // Para listas com validação condicional (ex: certificado OU carta de experiência):
  // considera válido se todos os obrigatórios estão preenchidos OU
  // se existe pelo menos um documento "opcional de par" preenchido quando aplicável.
const todosObrigatoriosEnviados = useMemo(() => {
  return documentos.filter((d) => d.obrigatorio).every((d) =>
    d.arquivos ? d.arquivos.length > 0 : d.arquivo !== null
  );
}, [documentos]);

  const handleFileChange = (docId: string, e: React.ChangeEvent<HTMLInputElement>) => {
  const doc = documentos.find((d) => d.id === docId);
  if (!doc) return;

  const isMulti = doc.documentType === 'certificado' || doc.documentType === 'diploma';
  const files = isMulti
    ? Array.from(e.target.files ?? [])
    : [e.target.files?.[0]].filter(Boolean) as File[];

  if (!files.length) return;

  for (const file of files) {
    const tamanhoMB = file.size / (1024 * 1024);
    if (tamanhoMB > doc.tamanhoMaxMB) {
      setErros((prev) => ({ ...prev, [docId]: `Arquivo muito grande. Máximo: ${doc.tamanhoMaxMB}MB` }));
      onTrackingError?.('file_too_large', doc.documentType, { maxSizeMb: doc.tamanhoMaxMB });
      return;
    }
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!doc.tiposAceitos.includes(ext)) {
      setErros((prev) => ({ ...prev, [docId]: `Tipo não aceito. Aceitos: ${doc.tiposAceitos.join(', ')}` }));
      onTrackingError?.('invalid_file_type', doc.documentType, { extension: ext });
      return;
    }
  }

  setErros((prev) => ({ ...prev, [docId]: '' }));
  setUploadStatus((prev) => ({ ...prev, [docId]: 'idle' }));
  setDocumentos((prev) => prev.map((d) =>
    d.id === docId
      ? isMulti ? { ...d, arquivos: files } : { ...d, arquivo: files[0] }
      : d
  ));
};

  const uploadSingle = async (doc: Documento, affiliateIdToUse: string): Promise<UploadResult> => {
    if (!doc.arquivo) throw new Error('arquivo ausente');

    const url =
      `${API_BASE}/documents/public-upload` +
      `?affiliateId=${encodeURIComponent(affiliateIdToUse)}` +
      `&documentType=${encodeURIComponent(doc.documentType)}` +
      `&description=${encodeURIComponent(doc.titulo)}`;

    const form = new FormData();
    form.append('file', doc.arquivo);

    const res = await fetch(url, { method: 'POST', body: form });

    if (!res.ok) {
      let msg = `Upload falhou (${res.status})`;
      try { const j = await res.json(); msg = j?.message || msg; } catch {}
      throw new Error(msg);
    }

    return (await res.json()) as UploadResult;
  };

 const handleSubmit = async () => {
  const faltantes = documentos.filter((d) =>
    d.obrigatorio && (d.arquivos ? d.arquivos.length === 0 : !d.arquivo)
  );
  if (faltantes.length > 0) {
    onTrackingError?.('missing_required_documents', 'documentos', {
      documentTypes: faltantes.map((documento) => documento.documentType),
    });
    alert(`Documentos obrigatórios faltantes: ${faltantes.map((d) => d.titulo).join(', ')}`);
    return;
  }

  setUploading(true);
  setErros((prev) => ({ ...prev, submit: '' }));

  try {
    let affiliateIdToUse = effectiveAffiliateId;

    if (!affiliateIdToUse) {
      if (!onGerarAffiliateId) {
        onTrackingError?.('registration_not_created', 'documentos');
        alert('Cadastro ainda não foi criado. Volte e envie a solicitação, ou conecte o onGerarAffiliateId no formulário.');
        setUploading(false);
        return;
      }
      const created = await onGerarAffiliateId();
      affiliateIdToUse = created.affiliateId;
      setLocalAffiliateId(created.affiliateId);
    }

    const results: UploadResult[] = [];

    for (const doc of documentos) {
      const temArquivos = doc.arquivos?.length ? doc.arquivos : doc.arquivo ? [doc.arquivo] : [];
      if (!temArquivos.length) continue;
      for (const arquivo of temArquivos) {
        const docComArquivo = { ...doc, arquivo };
        try {
          const r = await uploadSingle(docComArquivo, affiliateIdToUse);
          results.push(r);
          setUploadStatus((prev) => ({ ...prev, [doc.id]: 'ok' }));
        } catch (err: any) {
          setUploadStatus((prev) => ({ ...prev, [doc.id]: 'fail' }));
          setErros((prev) => ({ ...prev, [doc.id]: err?.message || 'Erro no upload' }));
          onTrackingError?.('upload_failed', doc.documentType);
          throw err;
        }
      }
    }

    const arquivos = documentos.map((d) => d.arquivo).filter((a): a is File => a !== null);
    onDocumentosSalvos(arquivos);
    onUploadSucesso?.(results);

    alert('Uploads enviados com sucesso ✅');
  } catch (error: any) {
    setErros((prev) => ({ ...prev, submit: error?.message || 'Falha ao enviar documentos' }));
  } finally {
    setUploading(false);
  }
};
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Documentos Necessários</h2>
        <p className="text-gray-600">Envie os documentos solicitados abaixo</p>

        {!effectiveAffiliateId && (
          <div className="mt-3 flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <AlertCircle className="w-4 h-4" />
            Ao clicar em <b>"Enviar Documentos"</b>, o sistema cria sua solicitação automaticamente e envia os arquivos.
          </div>
        )}

        {erros.submit && (
          <div className="mt-3 flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
            <AlertCircle className="w-4 h-4" />
            {erros.submit}
          </div>
        )}
      </div>

      {documentos.map((doc) => (
        <div key={doc.id} className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                {doc.titulo}
                {doc.obrigatorio && <span className="text-red-500 text-sm">*</span>}
              </h3>
              <p className="text-sm text-gray-600 mt-1">{doc.descricao}</p>
              <p className="text-xs text-gray-500 mt-2">
                Aceitos: {doc.tiposAceitos.join(', ')} | Máximo: {doc.tamanhoMaxMB}MB
              </p>
            </div>

            {uploading ? (
              <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
            ) : uploadStatus[doc.id] === 'ok' ? (
              <CheckCircle className="w-6 h-6 text-green-500" />
            ) : uploadStatus[doc.id] === 'fail' ? (
              <AlertCircle className="w-6 h-6 text-red-500" />
            ) : doc.arquivo ? (
              <CheckCircle className="w-6 h-6 text-green-500" />
            ) : null}
          </div>

          <div className="flex items-center gap-4">
            <label className="flex-1">
              <div className="flex items-center gap-3 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 cursor-pointer transition-colors">
                <Upload className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-600">
  {doc.arquivos?.length
    ? `${doc.arquivos.length} arquivo(s) selecionado(s)`
    : doc.arquivo
    ? doc.arquivo.name
    : 'Clique para selecionar arquivo'}
</span>
              </div>
              <input
  type="file"
  accept={doc.tiposAceitos.join(',')}
  multiple={doc.documentType === 'certificado' || doc.documentType === 'diploma'}
  onChange={(e) => handleFileChange(doc.id, e)}
  className="hidden"
  disabled={uploading}
/>
            </label>
            {doc.arquivo && <FileText className="w-5 h-5 text-blue-500" />}
          </div>

          {erros[doc.id] && (
            <div className="mt-3 flex items-center gap-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              {erros[doc.id]}
            </div>
          )}
        </div>
      ))}

      <div className="flex justify-end pt-6 border-t">
        <button
          onClick={handleSubmit}
          disabled={!todosObrigatoriosEnviados || uploading}
          className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
          {uploading ? 'Enviando...' : 'Enviar Documentos'}
        </button>
      </div>
    </div>
  );
};

export default UploadDocumentos;
